import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'baseui/snackbar'
import useDevTools from '@/containers/DevToolsNew/shared/useDevTools'
import {
  selectContactsItems,
  selectContactsPaginator,
  selectContactsQuery,
  selectContactsCountry,
  selectContactsOnlyWithContracts,
  selectContactsIsLoading,
  selectContactsError,
  selectContactsActionLoadingKey,
  selectContactsHasSearched,
  selectContactsLastActionAtByContractId,
} from './contactsSelectors'
import { setQuery, setCursor, setCountry, setOnlyWithContracts } from './contactsSlice'
import { contactsActions } from './contactsSagas'
import { buildCountrySelectOptions, flagByCountry } from '@/containers/DevToolsNew/Contacts/utils/contactsIcons'
import { buildContactRowViewModel } from '@/containers/DevToolsNew/Contacts/mappers/buildContactRowViewModel'

const useContacts = () => {
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { t } = useDevTools()
  const items = useSelector(selectContactsItems)
  const paginator = useSelector(selectContactsPaginator)
  const query = useSelector(selectContactsQuery)
  const country = useSelector(selectContactsCountry)
  const onlyWithContracts = useSelector(selectContactsOnlyWithContracts)
  const isLoading = useSelector(selectContactsIsLoading)
  const error = useSelector(selectContactsError)
  const actionLoadingKey = useSelector(selectContactsActionLoadingKey)
  const hasSearched = useSelector(selectContactsHasSearched)
  const lastActionAtByContractId = useSelector(selectContactsLastActionAtByContractId)
  const countrySelectOptions = useMemo(() => buildCountrySelectOptions(t), [t])

  const selectedCountryOption =
    countrySelectOptions.find(o => o.id === country) || countrySelectOptions[0]
  const countrySelectValue = [selectedCountryOption]

  const tableRows = useMemo(() => {
    const rawRows = Array.isArray(items) ? items : []

    return rawRows.map(row =>
      buildContactRowViewModel({
        row,
        t,
        actionLoadingKey,
        isGlobalLoading: Boolean(isLoading),
        dispatch,
        enqueue,
        flagByCountry,
        lastActionAtByContractId,
      }),
    )
  }, [items, t, actionLoadingKey, isLoading, dispatch, enqueue, lastActionAtByContractId])

  const canGoPrevious = !! paginator?.prev_cursor && ! isLoading
  const canGoNext = !! paginator?.next_cursor && ! isLoading

  const fetch = useCallback(() => {
    const trimmed = String(query || '').trim()
    if (trimmed.length < 3) return
    dispatch({ type: contactsActions.FETCH_CONTACT_CONTRACTS_LIST, enqueue, t })
  }, [dispatch, enqueue, query])

  const goNextPage = useCallback(() => {
    if (! canGoNext) return
    dispatch(setCursor(paginator.next_cursor))
    if (hasSearched) {
      dispatch({ type: contactsActions.FETCH_CONTACT_CONTRACTS_LIST, enqueue, t })
    }
  }, [canGoNext, paginator, dispatch, enqueue, hasSearched])

  const goPreviousPage = useCallback(() => {
    if (! canGoPrevious) return
    dispatch(setCursor(paginator.prev_cursor))
    if (hasSearched) {
      dispatch({ type: contactsActions.FETCH_CONTACT_CONTRACTS_LIST, enqueue, t })
    }
  }, [canGoPrevious, paginator, dispatch, enqueue, hasSearched])

  const refetchAfterFilterChange = useCallback(() => {
    if (! hasSearched) return
    if (String(query || '').trim().length < 3) return
    dispatch({ type: contactsActions.FETCH_CONTACT_CONTRACTS_LIST, enqueue, t })
  }, [dispatch, enqueue, hasSearched, query])

  const onCountrySelectChange = useCallback(
    value => {
      dispatch(setCountry(value?.[0]?.id ?? 'all'))
      refetchAfterFilterChange()
    },
    [dispatch, refetchAfterFilterChange],
  )

  const setQueryValue = useCallback(value => dispatch(setQuery(value)), [dispatch])
  const setOnlyWithContractsValue = useCallback(
    value => {
      dispatch(setOnlyWithContracts(value))
      refetchAfterFilterChange()
    },
    [dispatch, refetchAfterFilterChange],
  )

  return {
    filters: {
      query,
      onlyWithContracts,
      isLoading,
      onQueryChange: setQueryValue,
      onOnlyWithContractsChange: setOnlyWithContractsValue,
      onSearch: fetch,
    },

    countrySelect: {
      options: countrySelectOptions,
      value: countrySelectValue,
      onChange: onCountrySelectChange,
    },

    table: {
      rows: tableRows,
      pagination: {
        canGoPrevious,
        canGoNext,
        goPreviousPage,
        goNextPage,
      },
      isLoading,
      error,
    },
  }
}

export default useContacts
